import { getLocale } from '@libs/core';
import algoliasearch from 'algoliasearch';
import type { AlgoliaSearchOptions, SearchClient } from 'algoliasearch';

interface SearchClientOptions {
    admin?: boolean;
}

export const createAlgoliaClient = (
    opts: SearchClientOptions = { admin: false }
): SearchClient => {
    const locale = getLocale();

    if (!process.env.NEXT_PUBLIC_ALGOLIA_APP_ID) {
        throw new Error(`
            ERROR: The environment variable "NEXT_PUBLIC_ALGOLIA_APP_ID" is not set in
            the .env or .env.local file located at the root of the project.

            This variable is essential to the React InstantSearchNext Hooks Library.
            Please set it appropriately or disable the React InstantSearchNext Library.

            More info at: https://www.algolia.com/doc/api-reference/widgets/react-hooks/
        `);
    }

    if (!opts.admin && !process.env.NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY) {
        throw new Error(`
            ERROR: The environment variable "NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY" is not set in
            the .env or .env.local file located at the root of the project.

            This variable is essential to the React InstantSearchNext Hooks Library.
            Please set it appropriately or disable the React InstantSearchNext Library.

            More info at: https://www.algolia.com/doc/api-reference/widgets/react-hooks/
        `);
    }

    if (!opts.admin && !process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL) {
        throw new Error(`
            ERROR: The environment variable "NEXT_PUBLIC_ALGOLIA_HOST_URL" is not set in
            the .env or .env.local file located at the root of the project.

            This variable needs to be set to prevent the Algolia client from using the
            default URLs but instead use proxy URLs.

            This variable is essential to the React InstantSearchNext Hooks Library.
            Please set it appropriately or disable the React InstantSearchNext Library.

            More info at: https://www.algolia.com/doc/api-reference/widgets/react-hooks/
        `);
    }

    if (!opts.admin && !process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL_CA) {
        throw new Error(`
            ERROR: The environment variable "NEXT_PUBLIC_ALGOLIA_HOST_URL" is not set in
            the .env or .env.local file located at the root of the project.

            This variable needs to be set to prevent the Algolia client from using the
            default URLs but instead use proxy URLs.

            This variable is essential to the React InstantSearchNext Hooks Library.
            Please set it appropriately or disable the React InstantSearchNext Library.

            More info at: https://www.algolia.com/doc/api-reference/widgets/react-hooks/
        `);
    }

    if (opts.admin && !process.env.ALGOLIA_ADMIN_KEY) {
        throw new Error(`
            ERROR: The environment variable "ALGOLIA_ADMIN_KEY" is not set in
            the .env or .env.local file located at the root of the project.

            This variable is essential to interact with algolia in an admin level.
            Please set it appropriately.
        `);
    }

    const localeMap = {
        'en-US': process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL,
        'en-CA': process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL_CA,
    };

    const options: AlgoliaSearchOptions | undefined =
        typeof window !== 'undefined'
            ? ({
                  hosts: [{ url: localeMap[locale] }],
                  timeouts: {
                      connect: 30 as number, // increased from 2 due to slow proxy connection observed in local dev environments
                      read: 30 as number, // default
                      write: 30 as number, // default
                  },
              } as AlgoliaSearchOptions)
            : undefined;

    if (opts.admin) {
        return algoliasearch(
            process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
            process.env.ALGOLIA_ADMIN_KEY as string
        );
    }

    return algoliasearch(
        process.env.NEXT_PUBLIC_ALGOLIA_APP_ID as string,
        process.env.NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY as string,
        options
    );
};
